import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Users, CheckCircle2, Clock3, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, LineChart, Line } from "recharts";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

const STATUS_COLORS = {
  Draft: "#94a3b8", Submitted: "#3b82f6", "More Information Required": "#d97706",
  "Under Review": "#6366f1", Verified: "#15803d", Waitlisted: "#ea580c", Declined: "#b91c1c",
};

export default function AdminAnalytics() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setData(data)).catch(() => setError(true)); }, []);

  if (error) return (<div className="min-h-screen bg-[#FAF6F0]"><Navbar /><div className="p-20 text-center text-slate-500" data-testid="analytics-error">Could not load analytics. <button onClick={() => window.location.reload()} className="text-[#8B1E2B] underline">Retry</button></div></div>);
  if (!data) return (<div className="min-h-screen bg-[#FAF6F0]"><Navbar /><div className="p-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div></div>);

  const statusData = data.by_status.filter((s) => s.count > 0);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF6F0]">
      <Navbar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#8B1E2B]" data-testid="back-to-queue"><ArrowLeft className="h-4 w-4" /> {t("admin_queue")}</button>
        <h1 className="mt-4 font-serif text-3xl sm:text-4xl text-[#8B1E2B]" data-testid="analytics-title">{t("analytics")}</h1>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={<Users className="h-5 w-5" />} label={t("stat_total")} value={data.totals.submitted_or_beyond} testid="stat-total" />
          <Stat icon={<CheckCircle2 className="h-5 w-5" />} label={t("stat_verified")} value={data.totals.verified} testid="stat-verified" tone="emerald" />
          <Stat icon={<Clock3 className="h-5 w-5" />} label={t("stat_pending")} value={data.totals.pending_review} testid="stat-pending" tone="amber" />
          <Stat icon={<FileText className="h-5 w-5" />} label={t("stat_drafts")} value={data.totals.total_applications - data.totals.submitted_or_beyond} testid="stat-drafts" tone="slate" />
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Panel title={t("chart_by_status")}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={statusData} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.count}`}>
                  {statusData.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.status] || "#8B1E2B"} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {statusData.map((s) => (
                <span key={s.status} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_COLORS[s.status] }} /> {s.status} ({s.count})
                </span>
              ))}
            </div>
          </Panel>

          <Panel title={t("chart_by_location")}>
            {data.top_locations.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.top_locations} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="location" width={110} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#D4AF37" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <Panel title={t("chart_groom_age")}>
            {data.groom_age.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.groom_age}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} />
                  <Tooltip /><Bar dataKey="count" fill="#0A2540" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <Panel title={t("chart_bride_age")}>
            {data.bride_age.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.bride_age}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} />
                  <Tooltip /><Bar dataKey="count" fill="#8B1E2B" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>

          {data.submissions_by_day.length > 0 && (
            <Panel title={t("chart_over_time")} full>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.submissions_by_day}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} />
                  <Tooltip /><Line type="monotone" dataKey="count" stroke="#E65100" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Stat({ icon, label, value, testid, tone = "crimson" }) {
  const tones = { crimson: "text-[#8B1E2B] bg-[#8B1E2B]/10", emerald: "text-emerald-700 bg-emerald-100", amber: "text-amber-700 bg-amber-100", slate: "text-slate-600 bg-slate-100" };
  return (
    <div className="rounded-2xl bg-white border border-amber-500/20 p-5 shadow-sm" data-testid={testid}>
      <div className={`grid place-items-center h-10 w-10 rounded-xl ${tones[tone]}`}>{icon}</div>
      <div className="mt-3 text-3xl font-serif text-slate-900">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}
function Panel({ title, children, full }) {
  return (
    <div className={`rounded-2xl bg-white border border-amber-500/20 p-5 shadow-sm ${full ? "lg:col-span-2" : ""}`}>
      <h3 className="font-serif text-lg text-slate-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}
function Empty() { return <div className="h-40 grid place-items-center text-sm text-slate-400">No data yet</div>; }
